import React, { memo, useState, useCallback, useEffect } from 'react';
import CallLogsHeader from '../call-logs-header/call-logs-header';
import CallLogsSubHeader from '../call-logs-sub-header/call-logs-sub-header-container';
import VoicemailTemplatesTable from './voicemail-templates-table/voicemail-templates-table';
import './voicemail-templates.scss';
import VoicemailTemplatesEmptyState from '../call-logs-tab-empty-state-templates/voicemail-templates-empty-state';
import { RequestStatus } from '../../../../../../shared/enums/request-status';
import { PaginationOptionsChangePayload } from '../../../../../../shared/design-system/ui/tanstack-table/types';
import {
  CallLogsListCountsResponse,
  CreateVoicemailTemplateProps,
  GetPreSignedUrlResponse,
  GetSignedUrlForVoicemailTemplateResponse,
  GetVoicemailTemplateListProps,
  Template,
  UpdateVoicemailTemplateProps,
  VoicemailTemplateListResponse,
} from '../../../../types';
import RecordVoicemail from '../../../record-voicemail-popup/record-voicemail-container';
import { RequestState } from '../../../../../../shared/types/request-state';
import { executeOnRequestStatus } from '../../../../../../shared/utils';
import toaster from '../../../../../../shared/toaster';
import ConfirmationModalV3 from '../../../../../../shared/design-system/components/atoms/confirmation-modal/confirmation-modal-v3';
import { ConfirmationModalIconType } from '../../../../../../shared/design-system/components/atoms/confirmation-modal/enum';
import CallLogsSearchEmptyState from '../call-logs-tab-empty-state-templates/call-logs-search-empty-state';
import { getPreSignedUrl } from '../../../../helpers/dialer.api';
import { getAudioExtension } from '../../utils/helpers';
import hasPermission from '../../../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../../../shared/utils/access-control/enums/permissions';
import GlobalRestrictionModal from '../../../../../home/components/global-restriction-modal';

interface IProps {
  onMakeCallClick?: (show: boolean) => void;
  showDialer?: boolean;
  voicemailTemplateListResponse?: VoicemailTemplateListResponse;
  getVoicemailTemplateListRequestStatus?: string;
  getVoicemailTemplateListRequest?: (
    params: GetVoicemailTemplateListProps,
  ) => void;
  getSignedUrlForVoicemailTemplateResponses?: Record<
    number,
    GetSignedUrlForVoicemailTemplateResponse
  >;
  getSignedUrlForVoicemailTemplatePendingId?: number | null;
  getSignedUrlForVoicemailTemplateRequest?: (templateId: number) => void;
  createVoicemailTemplateRequest?: (
    payload: CreateVoicemailTemplateProps,
  ) => void;
  createVoicemailTemplateRequestStatus?: RequestState;
  resetCreateVoicemailTemplateRequest?: () => void;
  updateVoicemailTemplateRequest?: (params: {
    templateId: number;
    payload: UpdateVoicemailTemplateProps;
  }) => void;
  updateVoicemailTemplateRequestStatus?: RequestState;
  deleteVoicemailTemplateRequest?: (templateId: number) => void;
  deleteVoicemailTemplateRequestStatus?: RequestState;
  resetDeleteVoicemailTemplateRequest?: () => void;
  getSignedUrlForVoicemailTemplateRequestStatus?: RequestState;
  resetGetSignedUrlForVoicemailTemplateRequest?: () => void;
  resetUpdateVoicemailTemplateRequest?: () => void;
  resetGetSignedUrlForVoicemailTemplateResponses?: () => void;
  callLogsListCountsResponse?: CallLogsListCountsResponse;
  assignCallOutcomeRequestStatus?: RequestState;
  resetAssignCallOutcomeRequest?: () => void;
  createCallLogsNoteRequestStatus?: RequestStatus;
  resetCreateCallLogsNoteRequest?: () => void;
  isDemoAccount?: boolean;
}

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 25;

const VoicemailTemplates: React.FC<IProps> = ({
  onMakeCallClick = null,
  showDialer,
  voicemailTemplateListResponse,
  getVoicemailTemplateListRequestStatus,
  getVoicemailTemplateListRequest,
  getSignedUrlForVoicemailTemplateResponses,
  getSignedUrlForVoicemailTemplateRequest,
  createVoicemailTemplateRequest, // Reserved for create flow
  createVoicemailTemplateRequestStatus,
  resetCreateVoicemailTemplateRequest,
  updateVoicemailTemplateRequestStatus,
  deleteVoicemailTemplateRequest,
  deleteVoicemailTemplateRequestStatus,
  resetDeleteVoicemailTemplateRequest,
  resetGetSignedUrlForVoicemailTemplateRequest,
  updateVoicemailTemplateRequest,
  resetUpdateVoicemailTemplateRequest,
  resetGetSignedUrlForVoicemailTemplateResponses,
  callLogsListCountsResponse,
  assignCallOutcomeRequestStatus,
  resetAssignCallOutcomeRequest,
  createCallLogsNoteRequestStatus,
  resetCreateCallLogsNoteRequest,
  isDemoAccount,
}) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [showAddTemplateModal, setShowAddTemplateModal] = useState(false);
  const [editTemplate, setEditTemplate] = useState<Template | null>(null);
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(
    null,
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    null,
  );
  const [showGlobalRestrictionModal, setShowGlobalRestrictionModal] = useState(
    false,
  );

  useEffect(() => {
    getVoicemailTemplateListRequest?.({
      search: search || undefined,
      pageNumber: page,
      pageSize: limit,
    });
  }, [getVoicemailTemplateListRequest, search, page, limit]);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(DEFAULT_PAGE);
  }, []);

  const handleAddNewTemplateClick = useCallback(() => {
    setShowAddTemplateModal(true);
  }, []);

  const handleCloseAddTemplateModal = useCallback(() => {
    setShowAddTemplateModal(false);
  }, []);

  const handleEditTemplate = useCallback(
    (template: Template) => {
      setEditTemplate(template);
      getSignedUrlForVoicemailTemplateRequest?.(template.id);
    },
    [getSignedUrlForVoicemailTemplateRequest],
  );

  const handleCloseEditModal = useCallback(() => {
    setEditTemplate(null);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setTemplateToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (templateToDelete) {
      deleteVoicemailTemplateRequest?.(templateToDelete.id);
      setTemplateToDelete(null);
    }
  }, [templateToDelete, deleteVoicemailTemplateRequest]);

  const handleDeleteTemplate = useCallback(
    (template: Template) => {
      if (isDemoAccount) {
        setShowGlobalRestrictionModal(true);
        return;
      }
      setTemplateToDelete(template);
    },
    [isDemoAccount],
  );

  const handleCreateTemplate = useCallback(
    async (
      audioBlob: Blob | null,
      fileName: string,
      fileSize: number,
      duration: number,
      uploadType?: 'create' | 'update',
      existingAudioFileKey?: string,
      language?: string,
    ) => {
      try {
        // Title-only update: use existing audio file, skip upload
        if (uploadType === 'update' && editTemplate?.id && !audioBlob) {
          updateVoicemailTemplateRequest?.({
            templateId: editTemplate.id,
            payload: {
              title: fileName,
              audioFileKey: existingAudioFileKey,
              duration,
              language,
            },
          });
          return;
        }

        const uploadBody = audioBlob;
        if (!uploadBody) {
          throw new Error('Audio blob is missing for upload');
        }

        const fileExtension = fileName
          .substring(fileName.lastIndexOf('.'))
          .toLowerCase()
          .replace('.', '');
        const mimeType = fileExtension === 'wav' ? 'audio/wav' : 'audio/mpeg';

        // Step 1: Ask backend for pre-signed URL
        const preSignedUrlResponse = await getPreSignedUrl({
          fileName: getAudioExtension(fileName),
          documentType: 'voicemail-template',
          fileType: mimeType,
          fileSize,
          metadata: { duration },
        });

        const outerPayload = preSignedUrlResponse?.payload as GetPreSignedUrlResponse;
        const responseData = outerPayload?.payload;

        if (!responseData?.url || !responseData?.fileKey) {
          throw new Error('Failed to get pre-signed URL');
        }

        const { url: preSignedUrl, fileKey } = responseData;
        // Step 2: Upload the actual audio file/blob using the pre-signed URL
        const uploadResponse = await fetch(preSignedUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': mimeType,
            'Content-Length': uploadBody.size.toString(),
          },
          body: uploadBody,
        });
        if (!uploadResponse.ok) {
          throw new Error('Failed to upload file');
        }

        // Step 3: Call create or update API based on uploadType
        if (uploadType === 'update' && editTemplate?.id) {
          updateVoicemailTemplateRequest?.({
            templateId: editTemplate.id,
            payload: {
              title: fileName,
              audioFileKey: fileKey,
              duration,
              language,
            },
          });
        } else {
          createVoicemailTemplateRequest?.({
            title: fileName,
            audioFileKey: fileKey,
            duration,
            language,
          });
        }
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to save voicemail';
        toaster.error(errorMessage);
        throw error;
      }
    },
    [
      createVoicemailTemplateRequest,
      updateVoicemailTemplateRequest,
      editTemplate?.id,
    ],
  );

  const handlePaginationChange = useCallback(
    (payload: PaginationOptionsChangePayload) => {
      // Only reset page when limit actually changes; otherwise page changes get overwritten
      if (payload.limit != null && payload.limit !== limit) {
        setLimit(payload.limit);
        setPage(DEFAULT_PAGE);
        return;
      }

      if (payload.page != null && payload.page !== page) {
        setPage(payload.page);
      }
    },
    [page, limit],
  );
  const templates = voicemailTemplateListResponse?.templates ?? [];
  const totalRows = voicemailTemplateListResponse?.total ?? 0;
  const isLoading =
    getVoicemailTemplateListRequestStatus === RequestStatus.Pending;

  // Handle create template request
  useEffect(() => {
    executeOnRequestStatus({
      status: createVoicemailTemplateRequestStatus?.status,
      onSuccess: () => {
        handleCloseAddTemplateModal();
        toaster.success(createVoicemailTemplateRequestStatus?.message);
        getVoicemailTemplateListRequest?.({
          search: search ?? '',
          pageNumber: page,
          pageSize: limit,
        });
        resetCreateVoicemailTemplateRequest?.();
        resetGetSignedUrlForVoicemailTemplateResponses?.();
      },
      onFailed: () => {
        toaster.error(createVoicemailTemplateRequestStatus?.message);
        resetCreateVoicemailTemplateRequest?.();
        resetGetSignedUrlForVoicemailTemplateResponses?.();
      },
    });
  }, [createVoicemailTemplateRequestStatus]);

  // Handle update template request
  useEffect(() => {
    executeOnRequestStatus({
      status: updateVoicemailTemplateRequestStatus?.status,
      onSuccess: () => {
        handleCloseEditModal();
        toaster.success(updateVoicemailTemplateRequestStatus?.message);
        getVoicemailTemplateListRequest?.({
          search: search ?? '',
          pageNumber: page,
          pageSize: limit,
        });
        resetUpdateVoicemailTemplateRequest?.();
        resetGetSignedUrlForVoicemailTemplateResponses?.();
      },
      onFailed: () => {
        toaster.error(updateVoicemailTemplateRequestStatus?.message);
        resetUpdateVoicemailTemplateRequest?.();
      },
    });
  }, [updateVoicemailTemplateRequestStatus]);

  // Handle delete template request
  useEffect(() => {
    executeOnRequestStatus({
      status: deleteVoicemailTemplateRequestStatus?.status,
      onSuccess: () => {
        toaster.success(deleteVoicemailTemplateRequestStatus?.message);
        getVoicemailTemplateListRequest?.({
          search: search || undefined,
          pageNumber: page,
          pageSize: limit,
        });
        resetDeleteVoicemailTemplateRequest?.();
      },
      onFailed: () => {
        toaster.error(deleteVoicemailTemplateRequestStatus?.message);
        resetDeleteVoicemailTemplateRequest?.();
      },
    });
  }, [deleteVoicemailTemplateRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: assignCallOutcomeRequestStatus?.status,
      onSuccess: () => {
        toaster.success(assignCallOutcomeRequestStatus?.message);
        resetAssignCallOutcomeRequest();
      },
      onFailed: () => {
        toaster.error(assignCallOutcomeRequestStatus?.message);
        resetAssignCallOutcomeRequest();
      },
    });
  }, [assignCallOutcomeRequestStatus?.status]);

  // reset Note Request Status
  useEffect(() => {
    executeOnRequestStatus({
      status: createCallLogsNoteRequestStatus,
      onSuccess: () => {
        toaster.success('Note saved successfully');
        resetCreateCallLogsNoteRequest();
      },
      onFailed: () => {
        resetCreateCallLogsNoteRequest();
      },
    });
  }, [createCallLogsNoteRequestStatus, resetCreateCallLogsNoteRequest]);

  return (
    <div className="voicemail-templates">
      <CallLogsHeader
        onMakeCallClick={onMakeCallClick}
        showDialer={showDialer}
        callLogsListCountsResponse={callLogsListCountsResponse}
      />
      <CallLogsSubHeader
        showFilter={false}
        search={search}
        onSearchChange={handleSearchChange}
        showAddNewTemplate={hasPermission(Permissions.VOICEMAIL_TEMPLATE_WRITE)}
        handleAddNewTemplate={handleAddNewTemplateClick}
        searchPlaceholder="Search by name"
      />
      <div className="voicemail-templates__content">
        {!isLoading && totalRows === 0 ? (
          <div className="call-logs__empty-section">
            {search ? (
              <CallLogsSearchEmptyState />
            ) : (
              <VoicemailTemplatesEmptyState />
            )}
          </div>
        ) : (
          <VoicemailTemplatesTable
            data={templates}
            isLoading={isLoading}
            page={page}
            limit={limit}
            totalRows={totalRows}
            onPaginationChange={handlePaginationChange}
            onEditTemplate={handleEditTemplate}
            onDeleteTemplate={handleDeleteTemplate}
            resetGetSignedUrlForVoicemailTemplateRequest={
              resetGetSignedUrlForVoicemailTemplateRequest
            }
            setSelectedTemplateId={setSelectedTemplateId}
            selectedTemplateId={selectedTemplateId}
          />
        )}
      </div>

      {/* Create or upload Voicemail Modal */}
      <RecordVoicemail
        show={showAddTemplateModal}
        onClose={handleCloseAddTemplateModal}
        onSave={handleCreateTemplate}
        fromSettings={true}
        isDemoAccount={isDemoAccount}
      />
      {/* Edit Voicemail Modal */}
      {editTemplate && (
        <RecordVoicemail
          show={!!editTemplate}
          onClose={handleCloseEditModal}
          onSave={handleCreateTemplate}
          templateId={editTemplate.id}
          defaultTitle={editTemplate.title}
          initialAudioUrl={
            getSignedUrlForVoicemailTemplateResponses?.[editTemplate.id]
              ?.signedUrl
          }
          existingAudioFileKey={editTemplate.voicemailAudioUrl}
          defaultDuration={editTemplate.duration}
          fromSettings={true}
          editMode={true}
          defaultLanguage={editTemplate.language || 'en-US'}
          isDemoAccount={isDemoAccount}
        />
      )}

      <ConfirmationModalV3
        show={templateToDelete !== null}
        title="Delete voicemail template?"
        content="Are you sure you want to delete this voicemail template? This action cannot be undone."
        className="voicemail-template-delete-modal"
        onClose={handleCloseDeleteModal}
        onSubmit={handleConfirmDelete}
        submitButtonText="Yes"
        cancelButtonText="Cancel"
        showCloseIcon={false}
        iconType={ConfirmationModalIconType.TRASH_RED}
      />
      <GlobalRestrictionModal
        show={showGlobalRestrictionModal}
        onClose={() => setShowGlobalRestrictionModal(false)}
        title="Deleting voicemail template is disabled in demo mode"
        content="This is a demo account with sample data. Signup to delete voicemail templates"
        primaryBtnLabel="Sign up"
      />
    </div>
  );
};

export default memo(VoicemailTemplates);
